import { Checkbox, Chip, RadioGroup } from "@design";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import type { AskUserQuestion } from "./AskUserCard";

/**
 * A question's options. Described options are rows (label over description): radios
 * when you pick one, checkboxes when you pick any. Plain options are Chips, with a
 * check on picked ones when several are allowed. A hint says which it is.
 */
export function AskUserOptions({
  question,
  selected,
  onToggle,
}: Readonly<{ question: AskUserQuestion; selected: string[]; onToggle: (label: string) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "askUser" });
  const options = question.options ?? [];
  if (options.length === 0) return null;
  const multiple = Boolean(question.multiple);
  const described = options.some((o) => o.description);
  const hint = multiple ? t("pickAny") : t("pickOne");

  let control: React.ReactNode;
  if (described && multiple) {
    control = (
      <div className="flex flex-col gap-2">
        {options.map((o) => (
          <Checkbox
            key={o.label}
            label={o.label}
            description={o.description}
            checked={selected.includes(o.label)}
            onChange={() => onToggle(o.label)}
          />
        ))}
      </div>
    );
  } else if (described) {
    control = (
      <RadioGroup
        aria-label={question.question}
        options={options.map((o) => ({ value: o.label, label: o.label, description: o.description }))}
        value={selected[0]}
        onChange={(label) => {
          if (!selected.includes(label)) onToggle(label);
        }}
      />
    );
  } else {
    control = (
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const picked = selected.includes(o.label);
          return (
            <Chip
              key={o.label}
              type="button"
              selected={picked}
              aria-pressed={picked}
              icon={multiple && picked ? <Check size={14} aria-hidden="true" /> : undefined}
              onClick={() => onToggle(o.label)}
            >
              {o.label}
            </Chip>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Text variant="caption" tone="tertiary">
        {hint}
      </Text>
      {control}
    </div>
  );
}
